import React from 'react';

export interface IOrbitMarkReplyProps {
  /** Drawn slot width. 14 in the assistant avatar (`5199:10088`). */
  width?: number | string;
  /** Drawn slot height. 15 in the assistant avatar (`5199:10088`). */
  height?: number | string;
  className?: string;
  /**
   * Ink for all five vectors. A literal, not a `var()` reference: an SVG `fill` attribute is not a
   * CSS declaration and does not substitute custom properties.
   */
  fill?: string;
}

/**
 * The Orbit mark at avatar size as the designer exported it, BRAND TERRACOTTA: Figma `5199:10088`,
 * five vectors in a 14 x 15 box. It sits beside every assistant reply in the thread and replaces
 * the gradient tile + white mark the thread wore before (user, 2026-09-21).
 *
 * The fill defaults to the drawn `#D2704C`, as the export carries it; `perx_3_redesign` passes the
 * light palette's `avatarAssistantMark` instead. The
 * exported `clipPath` is dropped - its rect is the whole viewBox, so it clipped nothing and only
 * risked a duplicate element id across the many replies a thread renders at once.
 */
const OrbitMarkReply: React.FC<IOrbitMarkReplyProps> = ({
  width = 14,
  height = 15,
  className,
  fill = '#D2704C'
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 14 15"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    focusable="false"
    aria-hidden="true"
    data-testid="orbit-mark-reply"
  >
    <path
      d="M11.5769 0C12.1025 0.806082 13.1667 1.02684 13.9585 0.496146L13.9677 0.489865C13.175 1.02856 12.9602 2.12128 13.4879 2.93052C12.9602 2.12128 11.8897 1.90196 11.097 2.44066C11.8897 1.90196 12.1046 0.809238 11.5769 0Z"
      fill={fill}
    />
    <path
      d="M13.2413 1.2373C9.18039 3.49883 3.09588 7.66298 2.20032 10.8052C2.03209 11.3955 1.8252 12.0258 1.82519 12.6409C1.82519 13.54 2.5652 14.2689 3.47808 14.2689C3.73993 14.2689 3.98755 14.2089 4.20757 14.1021C4.3133 14.0508 4.43295 14.0314 4.54527 14.0647C4.935 14.1805 4.9376 14.7061 4.54041 14.7909C3.24384 15.0676 1.593 15.1759 0.71685 14.4388C-0.795167 13.1668 0.4434 7.07366 13.2413 1.2373Z"
      fill={fill}
    />
    <path
      d="M10.0778 1.88982C10.3238 1.52138 10.2339 1.00926 9.8448 0.81043C8.94629 0.351377 8.09492 0.257523 6.95468 0.265056C5.53293 0.274449 4.14768 0.725584 2.98325 1.55843C1.81883 2.39128 0.930452 3.56636 0.43633 4.92729C0.0682482 5.94108 -0.0680581 7.02221 0.0316144 8.08913C0.08323 8.64161 0.680178 8.90087 1.14705 8.62253C1.46291 8.43425 1.60813 8.05373 1.56228 7.68119C1.46894 6.92279 1.6074 6.32501 1.91216 5.48568C2.29518 4.43075 2.9838 3.51989 3.88641 2.8743C4.78901 2.22872 5.86279 1.87902 6.96488 1.87174C7.77741 1.86637 8.34966 1.89265 8.95924 2.16074C9.3548 2.33473 9.83424 2.25462 10.0778 1.88982Z"
      fill={fill}
    />
    <path
      d="M11.3992 2.92173C11.7048 2.60968 12.2021 2.6071 12.472 2.95225C12.9295 3.53713 13.2951 4.19309 13.5542 4.89677C13.9051 5.84994 14.0524 6.86866 13.9863 7.8847C13.9201 8.90074 13.642 9.89062 13.1706 10.788C12.6992 11.6853 12.0453 12.4695 11.2528 13.0879C10.4603 13.7063 9.54754 14.1447 8.57553 14.3736C7.60358 14.6026 6.59494 14.6168 5.61713 14.4154C4.63934 14.214 3.71501 13.8016 2.90607 13.2058C2.31253 12.7687 1.79116 12.2402 1.35984 11.6407C1.1006 11.2804 1.23519 10.7807 1.61411 10.5592C1.98956 10.3398 2.4626 10.4779 2.73013 10.8266C3.04362 11.235 3.41259 11.5976 3.82696 11.9028C4.45401 12.3647 5.17051 12.6843 5.92842 12.8405C6.68641 12.9966 7.46825 12.9855 8.22168 12.8081C8.97511 12.6306 9.68264 12.2908 10.2969 11.8114C10.9113 11.3321 11.4181 10.7243 11.7835 10.0286C12.149 9.33304 12.3645 8.5657 12.4158 7.77814C12.4671 6.99058 12.3529 6.20092 12.0808 5.46204C11.9022 4.97665 11.658 4.52056 11.3566 4.1065C11.0956 3.74813 11.0902 3.23713 11.3992 2.92173Z"
      fill={fill}
    />
    <path
      d="M2.30577 7.7588C3.31043 6.76238 2.70487 5.71732 1.50447 6.4496L0.813213 6.83396C0.313714 7.11176 -0.0212714 7.6436 0.0275412 8.22548C0.0323622 8.28296 0.0377924 8.33858 0.0437718 8.39246C0.132575 9.19244 0.895557 9.24524 1.43438 8.66114C1.71762 8.35406 2.0093 8.0528 2.30577 7.7588Z"
      fill={fill}
    />
  </svg>
);

export default OrbitMarkReply;
